/** @jsxImportSource @emotion/react */
import tw from 'twin.macro';
import { css } from '@emotion/react';
import { ReactComponent as InfoIcon } from 'assets/Icons/info.svg';
import { Info } from 'components/shared/Info';
import { SortTable } from 'components/shared/SortTable/SortTable';
import { ReactComponent as RevokeKeySvg } from 'assets/Icons/revoke.svg';
import { ReactComponent as ApiKeySvg } from 'assets/Icons/key.svg';
import { ReactComponent as CopySvg } from 'assets/Icons/copy.svg';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'reducers';
import { toDateApiKey } from 'utils/date.util';
import { hoverDropShadow } from 'components/shared/twin.styles';
import { Hint } from 'components/shared/Hint';
import {
  appendApiKey,
  removeApiKey,
  setApiKeyList,
} from 'slices/api-key.slice';
import {
  SingleNameCreationModalRef,
  SingleNameCreationModal,
} from 'components/shared/SingleNameCreationModal/SingleNameCreationModal';
import { customToast } from 'utils/toast.util';
import { ApiKeyService } from 'services/api-keys.service';
import { useCopyToClipboard, useMount, useToggle } from 'react-use';
import Loader from 'components/loader/loader';
import { ConfirmModal } from 'components/shared/ConfirmModal/ConfirmModal';
import { ModalOptions, Roles } from 'utils/enum';
import { Column } from 'react-table';
import { ApiKey } from 'utils/models';
import { BasicConfirmModal } from 'utils/models/modal.model';
import { UsageTrackingSection } from './UsageTracking';
import { tierSelector } from 'slices/payment.slice';
import { ManageLimitsButton } from './ManageLimits/ManageLimitsButton';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { getUserRole } from 'utils/utils';

type Props = {};

export const ApiTab = (props: Props) => {
  const dispatch = useDispatch();

  const [, copyToClipboard] = useCopyToClipboard();

  const [isLoading, toggleLoading] = useToggle(true);

  const apiKey = useSelector((state: RootState) => state.apiKey);
  const tier = useSelector(tierSelector);
  
  const isRestricted = tier.isTrial || tier.isPAYG;
  // All non-Trial/PayG plans can manage limits and use full API features
  const canManageLimits = !isRestricted;

  const confirmDeleteModalRef = useRef<BasicConfirmModal>(null);
  const singleNameCreationModal = useRef<SingleNameCreationModalRef>(null);

  useMount(async () => {
    try {
      toggleLoading(true);
      const { data: response } = await ApiKeyService.getApiKeys();

      dispatch(setApiKeyList(response.data));
    } catch (err: any) {
      console.log('err :>> ', err);
    } finally {
      toggleLoading(false);
    }
  });

  const handleAdd = async () => {
    const result = await singleNameCreationModal.current?.show();

    if (result?.option !== ModalOptions.YES) return;

    const apiKeyName = result?.singleName?.trim() ?? '';

    const createApiKeyAsync = ApiKeyService.createNewApiKey(apiKeyName);

    customToast.promise(createApiKeyAsync, {
      loading: 'Generating API key...',
      success: 'API key generated',
      error: 'Failed to generate API key',
    });

    try {
      const { data: newApiKey } = await createApiKeyAsync;

      // Sync data with redux slice
      dispatch(appendApiKey(newApiKey.data));
    } catch (err: any) {
      console.log('err :>> ', err);
    }
  };

  const handleRemove = async (apiKey: string) => {
    const result = await confirmDeleteModalRef.current?.show();

    if (result !== ModalOptions.YES) return;

    try {
      const removeApiKeyAsync = ApiKeyService.revokeApiKey(apiKey);

      customToast.promise(removeApiKeyAsync, {
        loading: 'Deleting API key...',
        success: 'API key deleted',
        error: 'Failed to delete API key',
      });

      await removeApiKeyAsync;

      dispatch(removeApiKey(apiKey));
    } catch (err: any) {
      console.log('Revoking API Key error :>> ', err);
    }
  };

  const handleCopyKey = (apiKey: string) => {
    if (!apiKey) return;

    copyToClipboard(apiKey);
    customToast.success('Copied to clipboard');
  };


  return (
    <div tw="p-7 md-down:(p-4) sm-down:(p-3)">
      {/* Loading State */}
      {isLoading && (
        <div
          className="loader__component"
          tw="height[calc(100vh - 13rem)] top[13rem] opacity-100"
        >
          <Loader />
        </div>
      )}

      {/* Restricted Access Banner */}
      {isRestricted && (
        <div
          tw="mb-4 flex items-center justify-center gap-2 px-4 py-3 rounded border border-sonnant-purple-2"
          css={infoBannerCss}
        >
          <InfoIcon
            tw="flex-shrink-0"
            width={24}
            height={24}
            css={infoIconCss}
          />
          <div tw="text-15 text-sonnant-purple-2">
            API features are available exclusively on Standard and Enterprise
            plans.{' '}
            <Link
              to="/account-settings#billing"
              tw="text-sonnant-purple-2! underline hover:(text-sonnant-purple-3!)!"
            >
              <b>Upgrade now</b>
            </Link>{' '}
            to access these features.
          </div>
        </div>
      )}

      {/* Main Content */}
      <div css={isRestricted ? restrictedContentCss : undefined}>
        {/* Add API Key Button */}
        <button
          className="button btn-primary large"
          tw="flex items-center (max-width[unset] w-auto px-3.5)! md-down:(w-full justify-center)!"
          onClick={handleAdd}
          disabled={isRestricted}
        >
          <span tw="flex mr-2">
            <ApiKeySvg />
          </span>
          Add API key
        </button>

        {/* API Keys Table */}
        <div tw="mt-6 rounded-lg shadow bg-white overflow-hidden">
          <div tw="overflow-x-auto" css={[responsiveTableWrapper]}>
            <div css={[customTableStyle]}>
              <SortTable
                columns={[
                  { Header: 'Name', accessor: 'name' },
                  { Header: 'Truncated Key', accessor: 'api_key' },
                  { Header: 'Created Date', accessor: 'created_at' },
                  {
                    Header: 'Delete',
                    id: '',
                    styles: {
                      textAlign: 'center',
                      maxWidth: '2rem',
                    },
                  } as Column<ApiKey>,
                ]}
                data={apiKey.items}
              >
                {({ original: row }) => (
                  <tr>
                    <td
                      tw="font-medium whitespace-nowrap overflow-hidden text-overflow[ellipsis]"
                      title={row?.name}
                    >
                      {row?.name}
                    </td>
                    <td tw="font-medium">
                      <span tw="flex items-center gap-x-4 md-down:(flex-col items-start gap-y-2)">
                        <Hint text={row?.api_key} arrow notTransparent>
                          <span tw="cursor-pointer font-mono whitespace-nowrap">
                            ...{row?.api_key?.slice(-8)}
                          </span>
                        </Hint>
                        <Hint text="Copy to clipboard" arrow leaveDelay={0}>
                          <span
                            tw="(flex items-center space-x-1 text-13 color[#1779ba] hover:underline)!"
                            css={
                              !isRestricted
                                ? tw`cursor-pointer`
                                : tw`cursor-not-allowed`
                            }
                            onClick={() =>
                              !isRestricted && handleCopyKey(row?.api_key)
                            }
                          >
                            <CopySvg />
                            <span>copy</span>
                          </span>
                        </Hint>
                      </span>
                    </td>
                    <td tw="whitespace-nowrap">
                      {toDateApiKey(row.created_at)}
                    </td>
                    <td>
                      <div tw="flex w-full justify-center">
                        <Hint text="Remove API key">
                          <RevokeKeySvg
                            css={[
                              !isRestricted ? hoverDropShadow(0.2) : undefined,
                              isRestricted
                                ? tw`cursor-not-allowed`
                                : tw`cursor-pointer`,
                            ]}
                            onClick={() =>
                              !isRestricted && handleRemove(row.api_key)
                            }
                          />
                        </Hint>
                      </div>
                    </td>
                  </tr>
                )}
              </SortTable>
            </div>
          </div>

          {/* Empty State */}
          {isEmpty(apiKey.items) && (
            <div tw="p-4">
              <Info text="Nothing to show" />
            </div>
          )}
        </div>

        {/* Manage Limits Button - Hidden for User role */}
        {getUserRole() !== Roles.USER && !isRestricted && canManageLimits && !isEmpty(apiKey.items) && (
          <div tw="mt-6">
            <ManageLimitsButton />
          </div>
        )}

        {/* Usage Tracking Section - Hidden for User role*/}
        {getUserRole() !== Roles.USER && !isLoading && <UsageTrackingSection />}
      </div>

      {/* New API Key Modal */}
      <SingleNameCreationModal
        ref={singleNameCreationModal}
        title="New API key"
        label="Name"
        placeHolder="Give API key a name..."
        confirmText="Create API key"
      />

      {/* Confirm Delete Modal */}
      <ConfirmModal
        ref={confirmDeleteModalRef}
        message={
          <div>
            Removing an API Key will stop any programmatic access to the{' '}
            <b>Sonnant App</b> that uses this key.
          </div>
        }
        title="Remove API key"
        confirmText="Confirm"
      />
    </div>
  );
};

export const customTableStyle = css`
  table {
    width: 100%;
    min-width: 800px;
    table-layout: fixed;
    
    @media (max-width: 1024px) {
      min-width: 700px;
    }
    
    @media (max-width: 768px) {
      min-width: 650px;
    }
    
    @media (max-width: 640px) {
      min-width: 600px;
    }
  }

  table tr th {
    ${tw`(text-sonnant-grey-6 font-medium)!`}
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  table tr th,
  td {
    ${tw`(text-15)!`}
    padding: 1rem;
    
    @media (max-width: 1024px) {
      font-size: 0.9375rem;
      padding: 0.875rem 0.75rem;
    }
    
    @media (max-width: 768px) {
      font-size: 0.875rem;
      padding: 0.75rem 0.625rem;
    }
    
    @media (max-width: 640px) {
      font-size: 0.8125rem;
      padding: 0.625rem 0.5rem;
    }
  }
  
  /* Column widths */
  table tr th:nth-of-type(1),
  table tr td:nth-of-type(1) {
    width: 25%;
    max-width: 200px;
  }
  
  table tr th:nth-of-type(2),
  table tr td:nth-of-type(2) {
    width: 40%;
  }
  
  table tr th:nth-of-type(3),
  table tr td:nth-of-type(3) {
    width: 25%;
  }
  
  table tr th:nth-of-type(4),
  table tr td:nth-of-type(4) {
    width: 10%;
    min-width: 80px;
  }
`;

export const responsiveTableWrapper = css`
  -webkit-overflow-scrolling: touch;
  
  /* Show scrollbar on mobile */
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
  
  @media (max-width: 768px) {
    &::-webkit-scrollbar {
      height: 6px;
    }
  }
`;

const infoIconCss = css`
  path {
    fill: #5551ff;
  }
`;

const infoBannerCss = css`
  background-color: rgba(85, 81, 255, 0.1);
  border-color: rgba(85, 81, 255, 0.3);
`;

const restrictedContentCss = css`
  opacity: 0.5;
  pointer-events: none;
  user-select: none;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.4);
    z-index: 1;
  }
`;
